import { useCallback, useEffect, useState } from "react";
import api, { inr, formatDate } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";

export default function DistWallet() {
  const [data, setData] = useState(null);
  const [acct, setAcct] = useState(null);
  const { user } = useAuth();
  const { subscribe } = useRealtime();

  // R-11: Income Wallet + breakdown = Account Info numbers (same endpoint, same cutoff).
  const loadAcct = useCallback(() => {
    api.get("/dist/account-info", { params: { limit: 1 } }).then((r) => setAcct(r.data || null)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/dist/wallet").then((r) => setData(r.data));
    loadAcct();
  }, [loadAcct]);

  useEffect(() => {
    const off = subscribe("wallet.update", (evt) => {
      const { wallets, txn } = evt.data || {};
      setData((prev) => {
        const next = prev ? { ...prev } : { wallets: {}, transactions: [] };
        if (wallets) next.wallets = { ...(next.wallets || {}), ...wallets };
        if (txn) {
          const exists = (next.transactions || []).some((t) => t.id === txn.id);
          if (!exists) next.transactions = [txn, ...(next.transactions || [])].slice(0, 100);
        }
        return next;
      });
      if (txn?.direction === "credit") {
        toast.success(`+₹${Number(txn.amount).toLocaleString("en-IN")} credited`, { description: txn.description });
      }
      loadAcct();
    });
    return off;
  }, [subscribe, loadAcct]);

  const w = data?.wallets || user?.wallets || { income: 0, shopping: 0, reward: 0, franchise: 0 };
  const meta = acct?.account_meta;
  const incomeNet = acct?.wallet?.pending_next_payout_net_rs;
  const up = meta?.upcoming || {};
  const n = (v) => Number(v || 0);
  const p = {
    direct_ref_rs: n(up.already_credited_unclosed_dref_rs),
    pair_income_rs: n(up.already_credited_unclosed_pair_rs) + n(up.projected_newly_matched_pair_rs),
    monthly_bonus_rs: n(up.already_credited_unclosed_bonus_rs) + n(up.projected_bonus_rs),
    other_rs: n(up.already_credited_unclosed_other_rs),
    earned_rs: n(up.earned_gross_rs ?? up.projected_gross_rs),
    withdrawn_rs: n(up.withdrawn_deduction_rs),
    gross_rs: n(up.projected_gross_rs),
  };
  const sinceLabel = String(meta?.last_paid_period_label || "").replace(/_/g, " ");

  return (
    <DashboardLayout title="My Wallets" subtitle="Income · Shopping · Reward · Franchise">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { k: "income", l: "Income", c: "border-gold" },
          { k: "shopping", l: "Shopping", c: "border-olive" },
          { k: "reward", l: "Reward", c: "border-rose" },
          { k: "franchise", l: "Franchise", c: "border-navy" },
        ].map((x) => (
          <div key={x.k} data-testid={`wallet-card-${x.k}`} className={`bg-white border ${x.c} border-l-4 border-y border-r p-5 rounded-sm`}>
            <div className="text-[10px] uppercase tracking-widest text-navy/50">{x.l} Wallet</div>
            <div data-testid={`wallet-balance-${x.k}`} className="text-3xl text-navy font-extrabold tabular-nums lining-nums mt-3">
              {x.k === "income" ? (acct ? inr(incomeNet || 0) : "…") : inr(w[x.k] || 0)}
            </div>
            {x.k === "income" ? (
              <div className="text-[10px] text-navy/50 mt-1" data-testid="wallet-income-caption">Pending next payout (net) · same as Account Info</div>
            ) : null}
          </div>
        ))}
      </div>

      {/* Wallet breakdown — ties the Income Wallet number to its 4 weekly-payout
          components (customer rule 18 Feb 2026). Startup / booster / self_retail
          stay in the incomes ledger for audit but are excluded from wallet.income. */}
      {Number(p.earned_rs || 0) > 0 && (
        <div className="bg-white border border-gold/60 rounded-sm overflow-hidden mb-6" data-testid="wallet-breakdown">
          <div className="px-5 py-3 border-b border-gold/40 bg-gold/5 flex items-center justify-between">
            <h3 className="font-serif text-base text-navy">Income Wallet Breakdown</h3>
            <span className="text-[10px] uppercase tracking-widest text-navy/50" data-testid="wb-since">Pending next payout{sinceLabel ? ` · since ${sinceLabel}` : ""}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-border">
                  <td className="px-5 py-2.5 text-navy font-medium">Direct Referral 10% <span className="ml-1 text-[10px] uppercase tracking-widest bg-olive/10 text-olive px-1.5 py-0.5 rounded">Paid instantly</span></td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-olive font-semibold" data-testid="wb-direct-ref">{inr(p.direct_ref_rs)}</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-5 py-2.5 text-navy font-medium">Pair / Matching (1:1) <span className="ml-1 text-[10px] uppercase tracking-widest bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">Pending · Friday closing</span></td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-amber-700 font-semibold" data-testid="wb-pair">{inr(p.pair_income_rs)}</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-5 py-2.5 text-navy font-medium">Monthly Bonus <span className="ml-1 text-[10px] uppercase tracking-widest bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">Pending · Friday closing</span></td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-amber-700 font-semibold" data-testid="wb-monthly">{inr(p.monthly_bonus_rs)}</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-5 py-2.5 text-navy font-medium">Other (rank / retail)</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-navy/70 font-semibold" data-testid="wb-rank">{inr(p.other_rs)}</td>
                </tr>
                {p.withdrawn_rs > 0 && (
                  <tr className="border-b border-border bg-rose-50/40">
                    <td className="px-5 py-2.5 text-navy font-medium">Less: already withdrawn <span className="ml-1 text-[10px] uppercase tracking-widest bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Self-service payout</span></td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-rose-700 font-semibold" data-testid="wb-withdrawn">− {inr(p.withdrawn_rs)}</td>
                  </tr>
                )}
                <tr className="bg-navy">
                  <td className="px-5 py-3 text-gold font-extrabold uppercase tracking-wide text-xs">Income Wallet Total</td>
                  <td className="px-5 py-3 text-right tabular-nums text-gold font-extrabold text-base" data-testid="wb-total">{inr(p.gross_rs)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="px-5 py-2 text-[10px] text-navy/50 italic bg-navy/5 border-t border-border">
            Startup Income + Booster credits appear in Transactions below for audit but are excluded from this wallet (they are one-time perks, not part of weekly payout).
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-serif text-lg text-navy">Transactions</h3>
          <p className="text-[11px] text-navy/50 mt-0.5">Historical incomes ledger — all credits including startup + booster. May not sum to the Income Wallet number above; see breakdown for the weekly-payout formula.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy/5 text-navy">
              <tr>
                {["Date", "Wallet", "Type", "Description", "Amount"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.transactions || []).length === 0 && <tr><td colSpan={5} className="p-6 text-center text-navy/50">No transactions</td></tr>}
              {(data?.transactions || []).map((t) => (
                <tr key={t.id} data-testid={`txn-${t.id}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 text-navy/70">{formatDate(t.created_at)}</td>
                  <td className="px-4 py-3 capitalize text-navy">{t.wallet}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${t.direction === "credit" ? "bg-olive/10 text-olive" : "bg-rose/10 text-rose"}`}>{t.direction}</span>
                  </td>
                  <td className="px-4 py-3 text-navy/70">{t.description}</td>
                  <td className={`px-4 py-3 font-semibold ${t.direction === "credit" ? "text-olive" : "text-rose"}`}>
                    {t.direction === "credit" ? "+" : "−"}{inr(t.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
